import { useState } from "react";
import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { JobRequisition } from "@/types/recruitment";
import { Briefcase, Plus, MapPin, Users } from "@phosphor-icons/react";
import { toast } from "sonner";

export function JobBoard() {
  const [jobs, setJobs] = useKV<JobRequisition[]>("recruitment-jobs", [
    {
      id: "job-1",
      title: "Senior Frontend Engineer",
      department: "Engineering",
      location: "Remote",
      type: "Full-time",
      status: "Open",
      postedDate: new Date().toISOString(),
      hiringManagerId: "user-1",
      description: "We are looking for a React expert...",
      requirements: ["React", "TypeScript", "Tailwind"],
      applicantCount: 12
    }
  ]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newJob, setNewJob] = useState<Partial<JobRequisition>>({});

  const handleCreateJob = () => {
    if (!newJob.title || !newJob.department) {
      toast.error("Please fill in required fields");
      return;
    }

    const job: JobRequisition = {
      id: crypto.randomUUID(),
      title: newJob.title,
      department: newJob.department,
      location: newJob.location || "Remote",
      type: (newJob.type as any) || "Full-time",
      status: "Open",
      postedDate: new Date().toISOString(),
      hiringManagerId: "CURRENT_USER",
      description: newJob.description || "",
      requirements: [],
      applicantCount: 0
    };

    setJobs([...jobs, job]);
    setIsCreateOpen(false);
    setNewJob({});
    toast.success("Job posted successfully");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Active Job Postings</h3>
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Post New Job
        </Button>
      </div>

      <div className="grid gap-4">
        {jobs.map((job) => (
          <Card key={job.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-semibold text-lg">{job.title}</h4>
                  <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                    <span className="flex items-center gap-1"><Briefcase /> {job.department}</span>
                    <span className="flex items-center gap-1"><MapPin /> {job.location}</span>
                    <span className="flex items-center gap-1"><Users /> {job.applicantCount} Candidates</span>
                  </div>
                </div>
                <Badge variant={job.status === 'Open' ? 'default' : 'secondary'}>
                  {job.status}
                </Badge>
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm">View Details</Button>
                <Button variant="outline" size="sm">Share Link</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Post New Job</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Job Title</Label>
                <Input 
                  value={newJob.title || ''} 
                  onChange={e => setNewJob({...newJob, title: e.target.value})}
                  placeholder="e.g. Product Manager" 
                />
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Input 
                  value={newJob.department || ''} 
                  onChange={e => setNewJob({...newJob, department: e.target.value})}
                  placeholder="e.g. Product" 
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Location</Label>
                <Input 
                  value={newJob.location || ''} 
                  onChange={e => setNewJob({...newJob, location: e.target.value})}
                  placeholder="e.g. Remote / New York" 
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={newJob.type} 
                  onValueChange={v => setNewJob({...newJob, type: v as any})}
                >
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Full-time">Full-time</SelectItem>
                    <SelectItem value="Part-time">Part-time</SelectItem>
                    <SelectItem value="Contract">Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                className="h-32"
                value={newJob.description || ''} 
                onChange={e => setNewJob({...newJob, description: e.target.value})}
                placeholder="Job details..." 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateJob}>Post Job</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
